"use client";

import { Tabs, Text } from "@zapier/design-system";
import { ComponentProps, useState } from "react";
import { ZapCardTemplate } from "src/components/ZapCard";

import {
  UserInteractionEventName,
  emitUserInteractionEvent,
} from "src/observability/tracking";
import styles from "./TemplateShowcase.module.css";
import { ShowcaseCard } from "./ShowcaseCard";
import classNames from "classnames";

export type TemplateShowcaseItem = {
  template: ZapCardTemplate | undefined;
  name?: string;
  slug?: string;
};

type Props = {
  items: TemplateShowcaseItem[] | undefined;
  titleType?: ComponentProps<typeof Text>["type"];
  className?: string;
};

export const TemplateShowcase = ({
  items,
  titleType = "ParagraphHeader3",
}: Props) => {
  if (!items || items.length === 0 || !items[0].template) return null;

  if (items.length === 1) {
    return (
      <>
        <Text type={titleType}>Featured solution template</Text>
        <ShowcaseCard
          template={items[0].template}
          className={classNames(styles.singleShowcaseCard)}
        />
      </>
    );
  }

  const showcaseItems = items.filter(
    (item): item is TemplateShowcaseItem & { slug: string } => !!item.slug,
  );
  const [selectedUseCaseSlug, setSelectedUseCaseSlug] = useState(
    showcaseItems[0].slug!,
  );
  const selectedUseCase = showcaseItems.find(
    (useCase) => useCase.slug === selectedUseCaseSlug,
  );

  const handleChooseItem: ComponentProps<typeof Tabs>["onChooseItem"] = (
    item,
    e,
  ) => {
    setSelectedUseCaseSlug(item.href); // slug
    emitUserInteractionEvent(
      UserInteractionEventName.templateShowcaseTabClicked,
      {
        event_action: "click",
        option_selected: item.href,
      },
    );
    // skip URL change
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div>
      <Text type={titleType}>Featured solution templates</Text>
      {/* Tablet/Desktop */}
      <div className={styles.hiddenOnMobile}>
        <Tabs
          items={showcaseItems.map((el, i) => {
            return {
              label: el.name,
              text: el.name,
              href: el.slug!,
              isExtra: i > 4,
            };
          })}
          onChooseItem={handleChooseItem}
          selectedItemHref={selectedUseCaseSlug}
        />
      </div>
      {/* Mobile */}
      <div className={styles.showOnMobile}>
        <Tabs
          items={showcaseItems.map((el) => {
            return {
              label: el.name,
              text: el.name,
              href: el.slug,
            };
          })}
          onChooseItem={handleChooseItem}
          selectedItemHref={selectedUseCaseSlug}
        />
      </div>
      {selectedUseCase?.template && (
        <ShowcaseCard
          template={selectedUseCase.template}
          className={styles.multiShowcaseCard}
        />
      )}
    </div>
  );
};
